import React, { useEffect, useState } from 'react';
import {
	LoadingOverlay,
	Button,
	Title,
	Text,
	useMantineTheme,
	PasswordInput,
	Notification,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validatePassword } from '@/utils/utilities';
import { HeroImg } from '@/components/partials/heroImg/HeroImg';
import { AppPaths } from '@/configs/appPaths';
import { useContent } from '@/providers/ContentProvider';
import { SubmitStatus } from '../login/Login';
import { ErrorCode } from '@/types/types';
import UnauthenticatedApi from '@/api/UnauthenticatedApi';
import styles from './SetNewPassword.module.scss';

const initialValues = {
	password: '',
	repeatPassword: '',
};

const baseSlug = 'setPassword.';

export const SetNewPassword: React.FC = () => {
	const { getErrorMessage, getContent } = useContent();

	const { other } = useMantineTheme();

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	useEffect(() => {
		const token = searchParams.get('token');
		if (!token) navigate(`/${AppPaths.LOGIN}`);
	}, [navigate]);

	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [errorMessage, setErrorMessage] = useState('');

	const form = useForm({
		initialValues,
		validate: {
			password: (value: string, values: typeof initialValues) => {
				if (value !== values.repeatPassword)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
			repeatPassword: (value: string, values: typeof initialValues) => {
				if (value !== values.password)
					return getErrorMessage([ErrorCode.DIFFERENT_PASSWORDS]);
				if (!validatePassword(value))
					return getErrorMessage([ErrorCode.PASSWORD_TOO_WEAK]);
				return null;
			},
		},
	});

	const handleSubmit = async () => {
		setStatus(SubmitStatus.LOADING);

		const token = searchParams.get('token')!;

		const response = await UnauthenticatedApi.resetPassword(
			token,
			form.values.password
		);
		form.reset();

		if (response === SubmitStatus.SUCCESS) {
			setStatus(SubmitStatus.SUCCESS);
			return;
		}

		setErrorMessage(getErrorMessage(response.message));
		setStatus(SubmitStatus.ERROR);
	};

	return (
		<div className="page-wrapper-unauth">
			<form className={styles.form} onSubmit={form.onSubmit(handleSubmit)}>
				<LoadingOverlay visible={status === SubmitStatus.LOADING} />
				<Title ta="center" order={1} mb="md">
					{getContent(`${baseSlug}header`)}
				</Title>
				<Text size="lg" ta="center" c="dimmed" mb="xl">
					{getContent(`${baseSlug}subheader`)}
				</Text>
				{status === SubmitStatus.ERROR && (
					<Notification {...other.notificationProps.danger} mb="lg">
						<Text size="md">{errorMessage}</Text>
					</Notification>
				)}
				{status === SubmitStatus.SUCCESS && (
					<Notification {...other.notificationProps.success} mb="lg">
						<Text size="md">{getContent(`${baseSlug}success`)}</Text>
					</Notification>
				)}
				{status !== SubmitStatus.SUCCESS && (
					<>
						<PasswordInput
							{...form.getInputProps('password')}
							size="lg"
							required
							placeholder={getContent(`${baseSlug}inputPasswordPlaceholder`)}
							mb="lg"
						/>
						<PasswordInput
							{...form.getInputProps('repeatPassword')}
							size="lg"
							required
							placeholder={getContent(
								`${baseSlug}inputRepeatPasswordPlaceholder`
							)}
							mb="lg"
						/>
						<Button type="submit" mb="lg">
							{getContent(`${baseSlug}submitBtn`)}
						</Button>
					</>
				)}
				<Text size="md" ta="center" c="dimmed" mb="lg">
					{getContent(`${baseSlug}navigateLogin`, {
						links: {
							$login: {
								to: `/${AppPaths.LOGIN}`,
								className: 'injected-anchor ',
							},
						},
					})}
				</Text>
			</form>
			<HeroImg src={getContent(`${baseSlug}heroImg`)} />
		</div>
	);
};
